import React, { useState } from "react";
import { ApiCall } from "../../api/qc_client";
import "./ApiInspector.css";

interface ApiInspectorProps {
  calls: ApiCall[];
}

const ApiInspector: React.FC<ApiInspectorProps> = ({ calls }) => {
  const [selectedCall, setSelectedCall] = useState<ApiCall | null>(null);
  const [filterMethod, setFilterMethod] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");

  const filteredCalls = calls.filter((call) => {
    if (filterMethod && call.request.method !== filterMethod) return false;
    if (filterStatus) {
      if (filterStatus === "error" && !call.error) return false;
      if (filterStatus === "success" && (call.error || !call.response)) return false;
      if (filterStatus !== "error" && filterStatus !== "success") {
        const statusCode = call.response?.status;
        if (statusCode !== parseInt(filterStatus)) return false;
      }
    }
    return true;
  });

  const getMethodColor = (method: string): string => {
    switch (method) {
      case "GET":
        return "#3b82f6";
      case "POST":
        return "#10b981";
      case "PUT":
        return "#f59e0b";
      case "DELETE":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusColor = (status?: number): string => {
    if (!status) return "#ef4444";
    if (status < 300) return "#10b981";
    if (status < 400) return "#3b82f6";
    if (status < 500) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div className="api-inspector">
      <h2>API Inspector</h2>

      {/* Filters */}
      <div className="inspector-filters">
        <div className="filter-group">
          <label>Method:</label>
          <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
            <option value="">All</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All</option>
            <option value="success">Success (2xx)</option>
            <option value="error">Errors</option>
            <option value="400">400</option>
            <option value="401">401</option>
            <option value="404">404</option>
            <option value="409">409</option>
            <option value="500">500</option>
          </select>
        </div>
      </div>

      <div className="inspector-content">
        {/* Call List */}
        <div className="call-list">
          <h3>Calls ({filteredCalls.length})</h3>
          {filteredCalls.length === 0 ? (
            <p className="empty-state">No API calls yet</p>
          ) : (
            <div className="calls">
              {filteredCalls.map((call, idx) => (
                <div
                  key={idx}
                  className={`call-item ${selectedCall === call ? "selected" : ""}`}
                  onClick={() => setSelectedCall(call)}
                >
                  <div className="call-item-header">
                    <span
                      className="method"
                      style={{ backgroundColor: getMethodColor(call.request.method) }}
                    >
                      {call.request.method}
                    </span>
                    {call.response && (
                      <span
                        className="status"
                        style={{ backgroundColor: getStatusColor(call.response.status) }}
                      >
                        {call.response.status}
                      </span>
                    )}
                    {call.error && (
                      <span className="status error">ERROR</span>
                    )}
                    <span className="duration">
                      {call.response ? `${call.response.duration_ms}ms` : "pending"}
                    </span>
                  </div>
                  <div className="call-item-path">
                    {call.request.url.replace(/^https?:\/\/[^/]+/, "")}
                  </div>
                  <div className="call-item-time">
                    {new Date(call.request.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Call Details */}
        <div className="call-details">
          {selectedCall ? (
            <>
              <h3>Request Details</h3>
              <div className="details-section">
                <div className="details-item">
                  <label>Method:</label>
                  <code>{selectedCall.request.method}</code>
                </div>
                <div className="details-item">
                  <label>URL:</label>
                  <code className="url-code">{selectedCall.request.url}</code>
                </div>
                <div className="details-item">
                  <label>Headers:</label>
                  <pre>{JSON.stringify(selectedCall.request.headers, null, 2)}</pre>
                </div>
                {selectedCall.request.body && (
                  <div className="details-item">
                    <label>Body:</label>
                    <pre>{JSON.stringify(selectedCall.request.body, null, 2)}</pre>
                  </div>
                )}
              </div>

              {selectedCall.response ? (
                <>
                  <h3>Response Details</h3>
                  <div className="details-section">
                    <div className="details-item">
                      <label>Status:</label>
                      <code
                        style={{ color: getStatusColor(selectedCall.response.status) }}
                      >
                        {selectedCall.response.status}
                      </code>
                    </div>
                    <div className="details-item">
                      <label>Duration:</label>
                      <code>{selectedCall.response.duration_ms}ms</code>
                    </div>
                    <div className="details-item">
                      <label>Headers:</label>
                      <pre>{JSON.stringify(selectedCall.response.headers, null, 2)}</pre>
                    </div>
                    <div className="details-item">
                      <label>Body:</label>
                      <pre>{JSON.stringify(selectedCall.response.body, null, 2)}</pre>
                    </div>
                  </div>
                </>
              ) : null}

              {selectedCall.error ? (
                <>
                  <h3>Error Details</h3>
                  <div className="alert alert-error">{selectedCall.error}</div>
                </>
              ) : null}
            </>
          ) : (
            <p className="empty-state">Select a call to view details</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiInspector;
